// Customer/…/TrackPopup/TrackPopup.jsx
import React, { useMemo } from 'react';
import Classes from './styles/style.module.css';
import { motion } from 'framer-motion';
import { usePreventScrolling } from '../../../../../../../../Hooks/usePreventScrolling';
import { CloseIcon } from '../../../../../../../../utils/Icons';

const DB_TO_UI = {
    upcoming: 'Upcoming',
    on_the_way: 'On The Way',
    delayed: 'Delayed',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

function fmtDateTime(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true

    });
}
function fmtDate(dateStr, timeSlot) {
    if (!dateStr) return '—';
    try {
        const dt = new Date(dateStr);
        // If dateStr is plain "YYYY-MM-DD", it will be treated as local midnight.
        // That’s fine for display here; keep the timeSlot next to it.
        const datePart = dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        return timeSlot ? `${datePart} • ${timeSlot}` : datePart;
    } catch {
        return timeSlot ? `${dateStr} • ${timeSlot}` : dateStr;
    }
}


function to12hFromMin(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1; // 0→12, 13→1, etc.
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}
function to12h(hhmm) {
    const [h, m] = String(hhmm).split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return '—';
    return to12hFromMin(h * 60 + m);
}

/* Convert "HH:MM — HH:MM" (or "HH:MM-HH:MM") to 12h; no-op if already AM/PM */
function fmtTimeSlot12h(label) {
    if (!label) return '';
    if (/(am|pm)/i.test(label)) return label; // already 12h

    const sep = label.includes('—') ? '—' : (label.includes('-') ? '-' : null);
    if (sep) {
        const [a, b] = label.split(sep).map(s => s.trim());
        if (/^\d{1,2}:\d{2}$/.test(a) && /^\d{1,2}:\d{2}$/.test(b)) {
            return `${to12h(a)} ${sep} ${to12h(b)}`;
        }
    }
    if (/^\d{1,2}:\d{2}$/.test(label)) return to12h(label);
    return label;
}


export default function TrackPopup({ closePopup, submitPopup, booking }) {
    const handleBgClick = () => closePopup();
    const handleCardClick = (e) => e.stopPropagation();
    const handleSubmit = (e) => { e.preventDefault(); submitPopup?.(); };

    usePreventScrolling();

    // Build timeline steps from booking + statusUpdates
    const { steps, latestMsg, latestEta } = useMemo(() => {
        const b = booking || {};
        const updates = Array.isArray(b.statusUpdates) ? b.statusUpdates : [];

        // Helper to find date for a given "to" status from history
        const findWhen = (to) => {
            const u = updates.find(u => u.to === to);
            return u?.at || null;
        };

        // What’s current?
        const currDbStatus = b.status || 'upcoming';
        const currUiStatus = DB_TO_UI[currDbStatus] || 'Upcoming';

        // Determine which steps we want to show and their dates
        const createdAt = b.createdAt ? new Date(b.createdAt).toISOString() : null;
        const scheduledAt = b.appointment?.date ? b.appointment.date : null;
        const scheduledTimeSlot = fmtTimeSlot12h(b.appointment?.timeSlot || '');


        const onTheWayAt = findWhen('on_the_way');
        const delayedAt = findWhen('delayed');
        const completedAt = findWhen('completed');
        const cancelledAt = findWhen('cancelled');

        // Base timeline (we’ll omit cancelled/completed mutually)
        // Step status: 'done' (completed in past), 'current' (active), 'todo' (future)
        const stepsArr = [];

        // 1) Created
        stepsArr.push({
            key: 'created',
            label: 'Appointment Created',
            date: fmtDateTime(createdAt),
            status: 'done',
        });

        // 2) Scheduled/Upcoming
        const scheduledStatus =
            currDbStatus === 'upcoming' ? 'current'
                : ['on_the_way', 'delayed', 'completed'].includes(currDbStatus) ? 'done'
                    : currDbStatus === 'cancelled' ? 'done'
                        : 'current';
        stepsArr.push({
            key: 'scheduled',
            label: 'Scheduled',
            date: fmtDate(scheduledAt, scheduledTimeSlot),
            status: scheduledStatus,
        });

        // 3) On The Way (always include step so progress feels continuous)
        const onWayStatus =
            currDbStatus === 'on_the_way' ? 'current'
                : ['delayed', 'completed'].includes(currDbStatus) ? (onTheWayAt ? 'done' : 'todo')
                    : currDbStatus === 'cancelled' ? (onTheWayAt ? 'done' : 'todo')
                        : 'todo';
        stepsArr.push({
            key: 'on_the_way',
            label: 'On The Way',
            date: onTheWayAt ? fmtDateTime(onTheWayAt) : '—',
            status: onWayStatus,
        });

        // 4) Delayed (only show if there’s an event or currently delayed)
        if (currDbStatus === 'delayed' || delayedAt) {
            stepsArr.push({
                key: 'delayed',
                label: 'Delayed',
                date: delayedAt ? fmtDateTime(delayedAt) : '—',
                status: currDbStatus === 'delayed' ? 'current' : 'done',
            });
        }

        // 5) Terminal step: Completed OR Cancelled
        if (currDbStatus === 'cancelled' || cancelledAt) {
            stepsArr.push({
                key: 'cancelled',
                label: 'Cancelled',
                date: cancelledAt ? fmtDateTime(cancelledAt) : '—',
                status: currDbStatus === 'cancelled' ? 'current' : 'done',
            });
        } else {
            stepsArr.push({
                key: 'completed',
                label: 'Service Completed',
                date: completedAt ? fmtDateTime(completedAt) : '—',
                status: currDbStatus === 'completed' ? 'done' : 'todo',
            });
        }

        const latestMessage = b.statusMessage || '—';
        const latestEtaTxt = b.etaAt ? fmtDateTime(b.etaAt) : '—';

        // ── Progress math (compatible with your existing CSS) ──
        // count how many are 'done'
        return { steps: stepsArr, latestMsg: latestMessage, latestEta: latestEtaTxt, currUiStatus };
    }, [booking]);

    // Progress bar math (same logic as your original)
    const doneCount = steps.filter(s => s.status === 'done').length;
    const segments = Math.max(steps.length - 1, 1);
    const ratio = Math.max(0, Math.min(1, (doneCount - 1) / segments));
    const offsetPct = 100 / (steps.length * 2);
    const trackBasePct = 100 - offsetPct * 2;
    const fillPct = trackBasePct * ratio;

    const currUiStatus = DB_TO_UI[booking?.status] || 'Upcoming';

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className={Classes.popup} onClick={handleBgClick}
        >
            <motion.div
                initial={{ y: 7 }} animate={{ y: 0 }} exit={{ y: 7 }} transition={{ duration: 0.25 }}
                className={Classes.card} onClick={handleCardClick}
            >
                <div className={Classes.close} onClick={closePopup}>
                    <CloseIcon height="20px" fill="white" />
                </div>

                <div className={Classes.bgPopup}>
                    <div></div>
                    <h5 className={Classes.title}>Track Appointment</h5>
                    <div className={Classes.curveCon}></div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={Classes.cardCon}>
                        <div className={Classes.statusCon} >
                            <div>
                                <h4>Status:</h4>
                                <h5> {currUiStatus} </h5>
                            </div>
                            <div>
                                <h4>ETA:</h4>
                                <h5> {latestEta} </h5>
                            </div>
                            {/* <div style={{
                                background: 'rgba(0,0,0,0.04)',
                                border: '1px solid rgba(0,0,0,0.06)',
                                borderRadius: 8,
                                padding: '10px 12px'
                            }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Latest Update</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{latestMsg}</div>
                            </div> */}
                        </div>

                        {/* ───── Timeline / Progress ───── */}
                        <div className={Classes.progressWrap}>
                            <div className={Classes.progressTrack} style={{ left: `${offsetPct}%`, width: `${trackBasePct}%` }} />
                            <div className={Classes.progressFill} style={{ left: `${offsetPct}%`, width: `${fillPct}%` }} />
                            <div className={Classes.stepsRow}>
                                {steps.map((step) => {
                                    const isDone = step.status === 'done';
                                    const isCurrent = step.status === 'current';
                                    return (
                                        <div key={step.key} className={Classes.stepCol}>
                                            <div
                                                className={Classes.stepDate}
                                                title={step.date}
                                                style={{
                                                    fontSize: 11,
                                                    lineHeight: 1.2,
                                                    color: 'rgba(0,0,0,0.68)',
                                                    textAlign: 'center',
                                                    maxWidth: '120px',
                                                    margin: '0px auto 6px',
                                                    padding: 0,
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {step.date}
                                            </div>

                                            <div
                                                className={[
                                                    Classes.stepDot,
                                                    isDone ? Classes.stepDotDone : '',
                                                    isCurrent ? Classes.stepDotCurrent : '',
                                                ].join(' ')}
                                                aria-label={step.label}
                                                title={step.label}
                                            >
                                                {isDone && (
                                                    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                        <path d="M20.285 6.709a1 1 0 0 1 0 1.414l-9.193 9.193a1 1 0 0 1-1.414 0L3.715 11.55a1 1 0 1 1 1.414-1.414l5.255 5.255 8.486-8.486a1 1 0 0 1 1.415 0z" fill="currentColor" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div className={Classes.stepLabel}>{step.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <button type="button" onClick={handleSubmit} className={Classes.btn2}>
                            <h5>Return to dashboard</h5>
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div >
    );
}
